/* global sim */
import './pill.mjs';
import { create, is, removeDiacritics, registerCustomElement, createDateTimePicker } from '../utils.mjs';
import { createAutoComplete, createCoderAutoCompleteItems } from './autoComplete.mjs';
import {
	getRange,
	getCaret,
	setCaret,
	isSpacer,
	isDoubleSpacer,
	injectTextNode,
	completeFunctionSignature,
	copyImlFromRange,
	getTextFromClipboard,
	setTextToClipboard,
	getGroupIconSrc,
} from './coder.helpers.mjs';
import {
	astToDOM,
	domToAST,
	createPill,
	SPACER,
	resolveFullDOM,
	getIMLCategory,
	IML_CATEGORIES,
} from '../helpers/iml.mjs';
import { I18n } from '../helpers/i18n.mjs';
import './coder.scss';
import { IML } from '../deps/iml.mjs';
import FormanConfig from '../formanConfig.mjs';
import EventManager from '../eventManager.mjs';
import Features from '../features.mjs';
import { createAiBlock, handleAiBlockPaste, isAiBlock, MAPAI_FUNCTION_NAME, MAPAI_PINNED_KEY } from './aiBlock.mjs';
import { CoderHistory } from './coder-history.ts';

const config = FormanConfig.config.coder;

const RE_IML_GLOBAL = /{{([\s\S]*?)}}/g;

class CoderToken extends HTMLElement {
	constructor() {
		super();

		/** @type {() => void | null} */
		this.onRemove = null;
	}

	disconnectedCallback() {
		this.onRemove?.();
	}
}

registerCustomElement('imt-coder-token', CoderToken);

export class CoderPanelController {
	constructor(Coder, form, imlItems) {
		this.panel = null;
		this.form = form;
		this.imlItems = imlItems;
		this.isTemplater = this.form.parentElement?.classList?.contains('is-templater');

		this.variables = null;
		this.pills = null;

		this.state = {
			onPanel: false,
			keepFocused: false,
			onSearchInput: false,
			lastActiveCoder: null,
			lastActiveCoderCaretOffset: 0,
		};

		this._hintPanelTimer = null;

		this._bestPanelMasterCollapse = null;

		this._prebuildCommonPanelContent(Coder, form);
	}

	rerenderScenarioInputsPills() {
		const container = this.pillsBody.querySelector('#pills-integromat');

		if (!container) {
			return;
		}

		const pillsContainers = container.getElementsByClassName('coder-pill-group');
		const inputPillsContainer = Array.from(pillsContainers).filter(
			(el) => el.dataset.pillType === 'scenario_inputs',
		)[0];

		if (!inputPillsContainer) {
			return;
		}

		inputPillsContainer.innerHTML = '';

		const updatedScenarioInputs = this.form.meta.imlOptions.variables.find(
			(x) => x.category === 'scenario_inputs',
		).items;

		for (const [key, input] of Object.entries(updatedScenarioInputs)) {
			const pill = createPill('scenario_input', key, {
				imlItems: this.imlItems,
				inspector: this.form?.meta?.inspector,
			});

			pill.setAttribute('type', input.type);
			pill.setAttribute('group', input.group);
			pill._pill = input;

			inputPillsContainer.appendChild(pill);
		}
	}

	_resetBestPane() {
		const currentBestPane = this.pillsBody.querySelector(`#pills-best`);
		const bestPane = create('div#pills-best.tab-pane.tree[data-testid="pills-pane-best"]');
		const search = bestPane.appendChild(
			create('input.form-control.coder-search.focusable[placeholder="Search items"]'),
		);

		search.addEventListener('input', (event) => {
			this._searchPills(event, bestPane, this.isTemplater);
		});

		if (currentBestPane) {
			this.pillsBody.replaceChild(bestPane, currentBestPane);
		} else {
			this.pillsBody.append(bestPane);
		}
	}

	_prebuildCommonPanelContent(Coder, form) {
		this.pillsHeader = create('ul.nav.nav-tabs');
		this.pillsBody = create('div.tab-content');

		const bestNav = create('li.nav-item');
		const bestNavButton = create(`button.nav-link.pills-best[data-toggle="tab"][data-history="no"][data-testid="pills-tab-best"]`);
		bestNavButton.addEventListener('click', () => this._setTab('best'));
		bestNav.append(bestNavButton);

		this._setElementTooltip(I18n.l('coder.tabs.general'), bestNavButton, bestNav);

		this._resetBestPane();
		this.pillsHeader.append(bestNav);

		this._attachHintPanel();

		const tabs = ['general', 'math', 'string', 'date', 'array'];
		const customCategories = this.form.meta.imlOptions?.tabs || [];

		let allTabs = tabs;

		customCategories.forEach((t) => {
			if (isNaN(t.position) || t.position > allTabs.length - 1 || t.position < 0) {
				allTabs.push(t.name);
			} else {
				allTabs = [...allTabs.slice(0, t.position), t.name, ...allTabs.slice(t.position)];
			}
		});

		allTabs.forEach((group) => {
			// Nav items
			const item = create('li.nav-item');
			const link = create(`button.nav-link.pills-${group}[data-toggle="tab"][data-history="no"][group="${group}"][data-testid="pills-tab-${group}"]`);

			link.addEventListener('click', () => this._setTab(group));
			item.append(link);

			this.pillsHeader.append(item);

			// Tab panels
			const pane = create(`div#pills-${group}.tab-pane[data-testid="pills-pane-${group}"]`);
			const customCategory = customCategories.find((x) => x.name === group);

			if (customCategory) {
				if (group !== 'integromat') {
					link.classList.add('pills-custom');
					const icon = create(`i.fas.${customCategory.icon}`);

					link.appendChild(icon);
				}

				if (customCategory.label) {
					pane.appendChild(create(`h4 ${customCategory.label}`));
					this._setElementTooltip(customCategory.label, link, item);
				} else if (customCategory.name === 'ai') {
					this._setElementTooltip(I18n.l('coder.tabs.fillByAi'), link, item);
				}

				if (customCategory.extraElement instanceof HTMLElement) {
					pane.appendChild(customCategory.extraElement);
				} else if (typeof customCategory.extraElement === 'string') {
					pane.innerHTML = customCategory.extraElement;
				}
			} else {
				const text = I18n.l(`iml.${group}.label`, {}, true);
				pane.appendChild(create(`h4 ${text}`));
				this._setElementTooltip(text, link, item);
			}

			if (group === 'date') {
				pane.appendChild(create('h5#pickerLabel'));
				this.datePickerContainer = pane.appendChild(create('div.coder-pill-group.coder-datepicker'));
			}

			const getCustoms = (type) => {
				return this.form.meta.imlOptions?.[type]?.filter((x) => x.tab === group)?.map((x) => x.category) || [];
			};

			const customVariables = getCustoms('variables');
			const customFunctions = getCustoms('functions');

			const types = IML_CATEGORIES.concat(customVariables.concat(customFunctions));

			types.forEach((type, index) => {
				if (Coder[type]) {
					if (types.indexOf(type) !== index) {
						return;
					}
					const col = Object.entries(Coder[type])
						.filter(([name, elm]) => {
							const elmGroup = elm.getAttribute('group');

							if (elmGroup && elmGroup.includes(',')) {
								const groups = elmGroup.split(',');

								return groups.includes(group);
							}

							return elmGroup === group;
						})
						.map((x) => [x[0], x[1].cloneNode(true)]);

					if (!col.length) return;

					const customType = this.form?.meta?.imlOptions?.variables?.find((x) => x.category === type);

					pane.appendChild(create(`h5 ${customType?.label || type.replace('_', ' ')}`));

					const groupWrapper = pane.appendChild(create('div.coder-pill-group'));
					groupWrapper.setAttribute('data-pill-type', type);

					col.forEach(([name, elm]) => {
						if (type === 'variables') {
							const row = create('div.coder-pill-row');

							row.append(elm);
							groupWrapper.append(row);
						} else {
							groupWrapper.append(elm);
						}
					});
				}
			});

			this.pillsBody.appendChild(pane);
		});
	}

	_fillBestPane() {
		const bestPane = this.pillsBody.querySelector('#pills-best');
		const recursiveLookup = (group, variables = [], deep = 0) => {
			if (deep === 0) {
				const bundlePill = this.variables['`' + group.id + '`'];
				variables.push(bundlePill);
			}

			group.pills.forEach((pill) => {
				variables.push(this.variables[pill.code]);

				if (pill.pills) recursiveLookup(pill, variables, deep + 1);
			});

			return variables;
		};

		const pills = this.pills.filter((group) => group.pills && group.pills.length);

		if (!pills.length) {
			this._setTab('general');
		} else {
			this._setTab('best');

			if (pills.length) {
				this._bestPanelMasterCollapse = bestPane.appendChild(create('imt-coder-panel-master-expander'));
			}

			pills.forEach((group, i) => {
				const pillsGroup = bestPane.appendChild(create('div.coder-pill-container'));

				// if (i > 2) pillsGroup.classList.add('collapsed');

				const title = pillsGroup.appendChild(create('div.coder-pill-container-title'));

				// TODO Send theme, name and enlarge in root not on every pill
				const referenceNode = group.pills[0].node;

				const icon = title.appendChild(create('div.coder-pill-container-icon'));
				const img = icon.appendChild(create('img'));

				const iconSrc = getGroupIconSrc(group);

				img.setAttribute('src', iconSrc);
				icon.style.backgroundColor = group.pills[0].theme.toHex();

				const label = title.appendChild(create(`h4 ${group.name} `));

				label.append(create(`span.square ${group.id}`), ` - ${group.description}`);

				title.addEventListener('mouseover', (event) => (referenceNode.enlarged = true));
				title.addEventListener('mouseout', (event) => (referenceNode.enlarged = false));

				title.addEventListener('click', (event) => {
					pillsGroup.classList.toggle('collapsed');
					this._bestPanelMasterCollapse?.resolveMode();
				});

				const createPillRow = (variable) => {
					if (!variable?._pillMeta) {
						return;
					}

					const row = create('div.coder-pill-row');

					row.setAttribute('level', variable._pillMeta.level);
					row._pillMeta = variable._pillMeta;

					const collapseLevel = 1;
					if (variable._pillMeta.nested) {
						row.classList.add('coder-pill-row-nested', `coder-level-${variable._pillMeta.level}`);
						row.setAttribute('nth', variable.getAttribute('nth'));

						if (
							variable.getAttribute('level') > collapseLevel ||
							'collection' !== variable.getAttribute('parent-type')
						) {
							row.classList.add('out');
						}
					}

					if (
						parseInt(variable.getAttribute('level')) < collapseLevel &&
						'collection' === variable.getAttribute('type')
					) {
						row.classList.add('open');
					}

					row.append(variable);

					if (variable.hasAttribute('has-children')) {
						row.classList.add('coder-pill-row-has-children');

						row.addEventListener('click', (event) => {
							if (event.target !== event.currentTarget) return;
							const collapse = row.classList.contains('open');

							this._togglePill(row, collapse);
						});
					} else {
						if (variable.sample) row.append(variable.sample);
					}

					pillsGroup.append(row);
				};

				recursiveLookup(group).forEach((variable) => {
					createPillRow(variable);
				});
			});
		}
	}

	_build() {
		if (this.panel) return;

		this._resetBestPane();

		const customHeadersLength = this.form.meta.imlOptions?.tabs?.filter((x) => x.name !== 'integromat')?.length || 0;

		this.panel = create('imt-panel.coder-pills');
		this.panel.controller = this;
		this.panel.width = 350 + 36 * customHeadersLength;
		this.panel.position = ['right', 'left'];
		this.panel.header.appendChild(this.pillsHeader);

		const close = create('button.close[type="button"]');

		close.append(create('i.far.fa-times'));
		close.addEventListener('click', (event) => {
			event.preventDefault();
			event.stopPropagation();
			this.panel.relative.focus();
			this.close();
		});
		this.panel.header.append(close);
		this._setElementTooltip(I18n.l('common.close'), close, this.panel.header, 8);

		const help = create('button.help[type="button"]');

		help.append(create('i.fas.fa-question'));
		help.addEventListener('click', (event) => {
			event.preventDefault();
			event.stopPropagation();

			// Use existing kb:// resolver as it is out of Forman's scope

			if ('undefined' !== typeof sim) {
				sim(window).openHelp(config.kb.path);
			}
		});
		this.panel.header.append(help);
		this._setElementTooltip(I18n.l('common.help'), help, this.panel.header, 8);

		this._fillBestPane();

		this.panel.body.append(this.pillsBody);
	}

	_attachEventListeners() {
		this.panel?.addEventListener('mousedown', this._handlePanelsMouseDown);
		this.panel?.addEventListener('mouseleave', this._handlePanelMouseLeave);
	}

	_detachEventListeners() {
		this.panel?.removeEventListener('mousedown', this._handlePanelsMouseDown);
		this.panel?.removeEventListener('mouseleave', this._handlePanelMouseLeave);
	}

	_handlePanelsMouseDown = (event) => {
		if (is(event.target, '.hint-close') || is(event.target.parentElement, '.hint-close')) {
			this.state.lastActiveCoder.fnPanelEnabled = false;
		}

		if (event.target.closest('.focusable')) {
			this.state.onSearchInput = true;
			return;
		}

		if (!event.target.draggable) {
			this.state.keepFocused = true;
		}

		this.state.onPanel = true;
	};

	_handlePanelMouseLeave = () => {
		this.state.onPanel = false;
	};

	open(coder) {
		// In case of contextual switch, swap the content of Best Pane
		if (this.pills !== coder.pills || this.variables !== coder.variables) {
			this.pills = coder.pills;
			this.variables = coder.variables;

			this._resetBestPane();
			this._fillBestPane();
		}

		this._build();

		// if the picker exists, remove it and recreate a new picker to maker sure it's always connected to correct input
		if (this.datePickerContainer.firstChild) {
			this.datePickerContainer.firstChild.remove();
			this.datePickerAbortController?.abort();
			this.datePickerAbortController = null;
		}
		const input = this.state?.lastActiveCoder?.closest('[field]');
		const { abortController } = createDateTimePicker(
			this.datePickerContainer.appendChild(create('div')),
			this.state.lastActiveCoder,
			{
				type: input._instructions.type,
				time: input._instructions.time,
				onChange: (event, formatedDate, oldFormattedDate) => {
					const caret = getCaret();

					if (!caret.node || caret.node.nodeType !== 3) return;

					if (event.oldDate) {
						const diff = event.date.diff(event.oldDate);
						const maxDiff = 1000 * 60 * 60 * 12; // 12 hours for AM/PM
						const prev = caret.node.textContent.substr(caret.offset - oldFormattedDate.length, oldFormattedDate.length);

						// time was incremented, update current date instead of inserting new date when old date exists
						if (Math.abs(diff) <= maxDiff && prev === oldFormattedDate) {
							caret.node.textContent = caret.node.textContent.replace(oldFormattedDate, formatedDate);
							setCaret(caret.node, caret.offset + formatedDate.length);
							return;
						}
					}

					injectTextNode(caret.node, caret.offset, document.createTextNode(formatedDate));
					if (input.onChange) {
						input.onChange();
					}
				},
			},
		);

		this.datePickerAbortController = abortController;
		if (input._instructions.type === 'time') {
			this.datePickerContainer.parentNode.querySelector('#pickerLabel').textContent = I18n.l(
				`panels.datetimepicker.timeTitle`,
			);
		} else {
			this.datePickerContainer.parentNode.querySelector('#pickerLabel').textContent = I18n.l(
				`panels.datetimepicker.dateTitle`,
			);
		}

		if (this.panel.parentNode && this.panel.relative === coder) return; // Prevents open already opened panel
		this.panel.relative = coder; // Necessary for position of the coder

		if (!this.panel.isOpen) {
			this.panel.open();

			// Panel added to the DOM, attach event listeners
			this._attachEventListeners();
		}
	}

	close() {
		this._hideHintPanel(this._hintPanel, true);
		this.panel?.close();

		// Panel removed from DOM, remove event listeners
		this._detachEventListeners();
	}

	_attachHintPanel() {
		this.form.addEventListener('mouseover', (event) => {
			if (event.target.nodeName !== 'IMT-PILL' || event.target.closest('.coder-hint') || event.target._hintPanel)
				return;

			const elm = event.target;
			let group = elm.getAttribute('group');
			const name = elm.getAttribute('name');
			const type = elm.getAttribute('type');

			const tabGroup = this?.pillsHeader?.querySelector('button.nav-link.active')?.getAttribute('group');

			if (elm._pill && elm._pill.node) elm._pill.node.enlarged = true;

			if (elm?._pill?.expand) {
				// do not show hint panel for pills that are expanded
				return;
			}

			if (group && group.includes(',')) {
				group = tabGroup ?? group.split(',')[0];
			}

			if (!group || name == null) {
				const pill = elm._pill;

				if (!pill || !pill.name) {
					return;
				}

				if (elm.classList.contains('bundle-pill')) {
					return this._showHintPanel(elm, {
						name: pill.name,
						help: 'Allows you to map a whole output of a module',
						type: pill.type,
					});
				}

				return this._showHintPanel(elm, {
					name: pill.name,
					help: pill.raw ? `Raw: ${pill.raw}` : undefined,
					type: pill.type,
				});
			}

			switch (true) {
				// Module variable
				case elm.hasAttribute('prefix'):
					return this._showHintPanel(elm, elm._pill);

				// IML Variable
				case elm.classList.contains('variable'):
					return this._showHintPanel(elm, {
						name,
						help: I18n.l(`iml.${group}.variables.${name}.help`),
						type,
					});

				case elm.classList.contains('function'):
					if (elm.classList.contains('custom-function')) {
						return this._showHintPanel(elm, {
							name: name + `(${elm.getAttribute('function-arguments')})`,
							help: elm.getAttribute('description'),
							type,
						});
					}

					return this._showHintPanel(elm, {
						name: I18n.l(`iml.${group}.functions.${name}.syntax`),
						help: I18n.l(`iml.${group}.functions.${name}.help`),
						example: I18n.l(`iml.${group}.functions.${name}.example`, {
							defaultValue: null,
							skipInterpolation: true,
						}),
						type,
					});

				case elm.classList.contains('operator'):
					return this._showHintPanel(elm, {
						name,
						help: I18n.l(`iml.${group}.operators.${name}.help`),
					});

				case elm.classList.contains('keyword'):
					this._showHintPanel(elm, {
						name: I18n.l(`iml.${group}.keywords.${name}.label`),
						help: I18n.l(`iml.${group}.keywords.${name}.help`),
					});

				case elm.classList.contains('custom-variable'):
					const help = elm.getAttribute('custom-variable_help');
					const value = elm.getAttribute('custom-variable_value');
					const variableType = elm.getAttribute('custom-variable_type');
					const isDynamicValue = [
						'executionId',
						'dataConsumed',
						'executionStartedAt',
						'operationsConsumed',
						'isDLQExecution',
						'executionType',
						'executionUrl',
						'executionName',
						'creditsConsumed',
					].includes(name);

					this._showHintPanel(elm, {
						name,
						help,
						type,
						variableType,
						variableValue: isDynamicValue ? { isDynamic: isDynamicValue } : value,
					});
			}
		});

		this.form.addEventListener('mouseout', (event) => {
			if (event.target.nodeName !== 'IMT-PILL') return;
			if (event.target._pill && event.target._pill.node) {
				event.target._pill.node.enlarged = false;
			}

			if (event.target?._hintPanel?.ignoreMouseLeave) {
				return;
			}

			this._hideHintPanel(event.target._hintPanel);
		});
	}

	_showHintPanel(relative, pill, immediately = false, ignoreMouseLeave = false, showCloseBtn = false) {
		if (this._hintPanel) {
			this._hideHintPanel(this._hintPanel, true);
		}

		clearTimeout(this._hintPanelTimer);

		this._hintPanelTimer = setTimeout(
			() => {
				const panel = create('imt-panel.coder-hint');

				if (showCloseBtn) {
					const panelClose = create('div.panel-close');
					const panelCloseBtn = create('button.close.hint-close');

					panelCloseBtn.appendChild(create('i.far.fa-times'));

					panelClose.appendChild(panelCloseBtn);
					panel.appendChild(panelClose);
				}

				// Create reference to panel in pill
				relative._hintPanel = panel;

				panel.width = 'auto';
				panel.position = relative.closest('#pills-best') ? ['left', 'right'] : ['top', 'bottom'];
				panel.relative = relative;
				panel.compact = true;
				panel.spacing = 9;
				panel._closeTimer = null;

				const relativeStyle = window.getComputedStyle(relative);

				panel.style.color = relativeStyle.getPropertyValue('color');
				panel.style.backgroundColor = relativeStyle.getPropertyValue('background-color');

				const header = create(`h6 ${pill.name}`);

				if (pill.args) {
					header.innerText += '(';
					pill.args.forEach((arg, index) => {
						const argElm = create(`span`);

						argElm.classList.add('argument');
						argElm.innerText = arg.trim();
						if (index < pill.args.length - 1) {
							argElm.innerText += ';';
						}

						if (pill.activeArg === index) {
							argElm.classList.add('active-argument');
						}

						header.append(argElm);
					});
					header.append(document.createTextNode(')'));

					panel.setActiveArg = (index) => {
						header.querySelectorAll('.argument').forEach((elm, idx) => {
							if (index === idx) {
								elm.classList.add('active-argument');
							} else {
								elm.classList.remove('active-argument');
							}
						});
					};
				}

				if (pill.type) {
					header.append(create(`small ${pill.type}`));
				}
				panel.body.append(header);

				if (pill.raw && pill.name !== pill.raw) {
					panel.body.append(create(`p.raw ${pill.raw}`));
				}

				if (pill.help) {
					panel.body.append(create(`p ${pill.help}`));
				}

				if (pill.variableValue || typeof pill.variableValue === 'boolean') {
					const p = create(`p.coder-hint-value`);

					p.append(create(`strong ${I18n.l('common.value')}`));
					if (pill.variableValue.isDynamic) {
						p.append(create(`i ${I18n.l('variables.dynamicValue')}`));
					} else {
						switch (pill.type) {
							case 'boolean':
								p.append(pill.variableValue.toString());
								break;
							default:
								p.append(pill.variableValue);
								break;
						}
					}

					panel.body.append(p);
				} else if (
					pill.variableType === 'scenario_variable' &&
					['id', 'name', 'url'].includes(pill.name) &&
					pill.variableValue === null
				) {
					const p = create(`p.coder-hint-value`);

					p.append(create(`strong ${I18n.l('common.value')}`));
					p.append(create(`i ${I18n.l('variables.scenarioValueUnavailable')}`));
					panel.body.append(p);
				}

				if (pill.example) {
					for (let example of pill.example.trim().split(/\n/g)) {
						/* eslint-disable no-useless-escape */
						example = example.match(/^([^\{]+)(?:\{([^\}]*)\})?$/);
						if (example) {
							const pre = create('pre');
							const code = create(`code {{${example[1]}}}`);

							code.form = relative.form;
							const { element } = normalize(code, undefined, true, this.imlItems, this.form?.meta?.inspector);
							pre.append(element);

							if (example[2]) pre.append(create(`div =${example[2]}`));

							panel.body.append(pre);
						}
					}
				}

				panel.addEventListener('mouseenter', (event) => clearTimeout(panel._closeTimer));

				if (!ignoreMouseLeave) {
					panel.addEventListener('mouseleave', (event) => this._hideHintPanel(panel));
				} else {
					panel.ignoreMouseLeave = true;
				}

				panel.open();
				this._hintPanel = panel;
			},
			immediately ? 0 : 750,
		);
	}

	_hideHintPanel(panel, immediately = false) {
		clearTimeout(this._hintPanelTimer);
		if (!panel) return;

		const hide = () => {
			this._hintPanel = null;
			panel.close();
			panel.relative._hintPanel = null;
		};

		if (immediately) {
			hide();
			return;
		}

		panel._closeTimer = setTimeout(hide, 250);
	}

	_setTab(name) {
		this.pillsHeader.querySelector('button.nav-link.active')?.classList.remove('active');
		this.pillsBody.querySelector('div.tab-pane.active')?.classList.remove('active');

		this.pillsHeader.querySelector(`button.pills-${name}`).classList.add('active');
		this.pillsBody.querySelector(`#pills-${name}`).classList.add('active');
	}

	/**
	 * Collapses/expands array or collection pill in coder panel
	 * @param {HTMLElement} row
	 * @param {boolean} collapse
	 * @private
	 */
	_togglePill(row, collapse) {
		if (collapse) {
			row.classList.remove('open');
		} else {
			row.classList.add('open');
		}

		const level = row._pillMeta.level + 1;
		let elm = row;
		let index = 1;

		while (elm) {
			elm =
				elm.nextElementSibling && elm.nextElementSibling.matches('.coder-pill-row-nested')
					? elm.nextElementSibling
					: null;
			if (!elm) break;
			if (elm._pillMeta.level < level) break;
			if (collapse) {
				if (elm._pillMeta.hasChildren) {
					elm.classList.remove('open');
				}

				elm.classList.add('out');
			} else {
				if (elm._pillMeta.level === level && elm._pillMeta.nth === index) {
					elm.classList.remove('out');
					index++;
				}
			}
		}
	}

	_searchPills(event, bestPane, isTemplater) {
		this._hideHintPanel(this._hintPanel, true);

		bestPane
			.querySelectorAll('.coder-pill-row, .coder-pill-container')
			.forEach((row) => row.classList.remove('search-hide'));

		const imlMatches =
			bestPane.querySelector('div.search-iml-matches') || bestPane.appendChild(create('div.search-iml-matches.mt-3'));

		imlMatches.innerHTML = '';

		const phrase = event.target.value;
		let words = removeDiacritics(phrase.trim().toLowerCase());

		if (words && words.length) {
			words = words.split(' ');

			bestPane.querySelectorAll('.coder-pill-row').forEach((elm) => {
				const text = ((elm?._pillMeta?.prefix || '') + removeDiacritics(elm.textContent)).toLowerCase();
				const lookup = words.filter((word) => !text.includes(word));

				if (lookup.length) {
					// Hide not matching pills
					elm.classList.add('search-hide');
				} else if (elm._pillMeta && elm._pillMeta.nested) {
					// Keep parent pill visible if child matches
					let currentLevel = elm._pillMeta.level;
					let e = elm.previousElementSibling;

					while (e) {
						// [level="${level - 1}"] -> look only for parents not for sibling objects/collections
						if (e._pillMeta && e._pillMeta.hasChildren && e._pillMeta.level === currentLevel - 1) {
							e.classList.remove('search-hide');
							this._togglePill(e, false);

							currentLevel = e._pillMeta.level;
						}

						if (currentLevel === 0) break;

						e = e.previousElementSibling;
					}
				}
			});

			// Hide containers with no matches or expand matches
			bestPane.querySelectorAll('.coder-pill-container').forEach((elm) => {
				let found = false;

				let e = elm.querySelector('.coder-pill-row');

				while (e && e.matches('.coder-pill-row')) {
					if (!e.classList.contains('search-hide')) found = true;
					e = e.nextElementSibling;
				}

				if (!found) {
					elm.classList.add('search-hide');
				} else {
					elm.classList.remove('search-hide');
					elm.classList.remove('collapsed');
				}
			});

			// SEARCH
			IML_CATEGORIES.concat(this.form?.meta?.imlOptions?.variables?.map((x) => x.category) || []).forEach((type) => {
				if (Coder[type]) {
					const matches = Object.entries(Coder[type])
						.filter(([name, elm]) => {
							const text = removeDiacritics(elm.textContent.toLowerCase());
							const lookup = words.filter((word) => !text.includes(word));

							if (elm._pill?.internal) {
								return false;
							}

							return !lookup.length;
						})
						.map(([name, elm]) => {
							const clone = elm.cloneNode(true);

							if (type === 'variables') {
								const row = create('div.coder-pill-row');

								row.append(clone);
								return row;
							} else {
								return clone;
							}
						});

					// match.every.. -> template fix
					if (
						matches.length &&
						(!isTemplater ||
							matches.every((elm) => !is(elm, '.custom-variable') || elm.hasAttribute('custom-iml_system')))
					) {
						const customType = this.form?.meta?.imlOptions?.variables?.find((x) => x.category === type);

						imlMatches.append(create(`h5 ${customType?.label || type.replace('_', ' ')}`));

						const groupWrapper = imlMatches.appendChild(create('div.coder-pill-group'));

						groupWrapper.append(...matches);
					}
				}
			});
		}
	}

	/**
	 * @param text {string}
	 * @param element {HTMLElement}
	 * @param parent {HTMLElement}
	 * @param offset {number}
	 * @private
	 */
	_setElementTooltip(text, element, parent, offset = 6) {
		if (!customElements.get('imt-tooltip')) {
			element.title = text;
			return;
		}

		const tooltip = document.createElement('imt-tooltip');
		tooltip.text = text;
		tooltip.placement = 'top';
		tooltip.offset = offset;
		// Only allow top or bottom as left and right placements can cover other elements and make them unclickable.
		tooltip.fallbackPlacements = ['bottom'];

		tooltip.appendChild(element);
		parent.appendChild(tooltip);
	}
}

class CoderPanelExpander extends HTMLElement {
	constructor() {
		super();
		this._built = false;
		this._mode = null;
		this._container = null;
		this._collapseItemSelector = ':scope > div.coder-pill-container';
	}

	set container(value) {
		this._collapseContainer = value;
	}

	set collapseItemSelector(selector) {
		this._collapseItemSelector = selector;
	}

	get mode() {
		return this._mode;
	}

	set mode(value) {
		if (this._mode === value) return;

		this._mode = value;
		this.classList[value === 'expand' ? 'add' : 'remove']('collapsed');
		this._title.textContent = I18n.l(`buttons.${value}All`);
	}

	connectedCallback() {
		if (this._built) return;
		this._built = true;

		if (!this._container) this._container = this.parentElement;

		this._title = this.appendChild(create('div.coder-master-expander-title'));

		this.mode = 'collapse';

		this.addEventListener('click', (event) => {
			this._container.querySelectorAll(this._collapseItemSelector).forEach((elm) => {
				elm.classList[this._mode === 'expand' ? 'remove' : 'add']('collapsed');
			});

			this.mode = this.mode === 'expand' ? 'collapse' : 'expand';
		});
	}

	resolveMode() {
		const oneItemNotCollapsed = this._container.querySelector(this._collapseItemSelector + ':not(.collapsed)');

		if (oneItemNotCollapsed) this.mode = 'expand';
	}
}

registerCustomElement('imt-coder-panel-master-expander', CoderPanelExpander);

export class Coder extends HTMLElement {
	constructor() {
		super();

		if (Features.isOn('coder-undo')) {
			this._coderHistory = new CoderHistory(this);
		}

		this._built = false;
		this.panelEnabled = true;
		this.fnPanelEnabled = true;

		this.addEventListener('keydown', (event) => {
			if (event.key === 'Escape' && this.panelController?._hintPanel) {
				this.panelController._hideHintPanel(this.panelController._hintPanel, true);
				this.fnPanelEnabled = false;
				return;
			}

			if (this.hasExpandedPill && event.key !== 'Tab') {
				event.preventDefault();
				return;
			}

			if (Features.isOn('coder-undo')) {
				// Undo: Cmd+Z / Ctrl+Z
				if ((event.metaKey || event.ctrlKey) && !event.shiftKey && event.key.toLowerCase() === 'z') {
					this._coderHistory.undo(event);
					return;
				}

				// Redo: Cmd+Shift+Z / Ctrl+Shift+Z / Ctrl+Y
				if (
					(event.ctrlKey && event.key.toLowerCase() === 'y') ||
					((event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === 'z')
				) {
					this._coderHistory.redo(event);
					return;
				}
			}

			if (this.hasAttribute('readonly') || this.hasAttribute('disabled')) {
				if (!event.ctrlKey && !event.metaKey && (event.which < 33 || event.which > 40)) {
					// Allow any key in combination with ctrl/command
					// Allow pageup/pagedown/home/end/arrow keys
					event.preventDefault();
					return;
				}
			}

			const caret = getCaret();

			if (!caret.node || !caret.collapsed) return;

			if (event.which === 37 || event.which === 8) {
				// Arrow left or backspace
				if (caret.offset > 0 && caret.node.textContent.charAt(caret.offset - 1) === SPACER) {
					// Move cursor to the left one place so we'll be jumping to the correct position
					setCaret(caret.node, --caret.offset, true);
				}
			} else if (event.which === 39 || event.which === 46) {
				// Arrow right or delete
				if (caret.offset < caret.node.textContent.length && caret.node.textContent.charAt(caret.offset) === SPACER) {
					// Move cursor to the left one place so we'll be jumping to the correct position
					setCaret(caret.node, ++caret.offset);
				}
			}

			if (event.which === 8) {
				// Backspace when caret is collapsed (no range selection is active)
				if (caret.offset === 0 && caret.node.previousSibling && caret.node.previousSibling.nodeType === 1) {
					// Deleting a pill to the left of the cursor
					caret.node.parentNode.removeChild(caret.node.previousSibling);
					event.preventDefault();
					EventManager.dispatch(this, 'input', { bubbles: true });
				} else if (caret.offset === 1 && caret.node.length === 1 && !caret.node.nextSibling) {
					// Removing last char of a text node - automatically transform to SPACER.
					// Chrome by default moves cursor to first text node to the left. That's not what we want.
					caret.node.textContent = SPACER;
					event.preventDefault();
					EventManager.dispatch(this, 'input', { bubbles: true });
				} else if (
					caret.offset === 1 &&
					caret.node.length === 1 &&
					caret.node.nextSibling &&
					caret.node.previousSibling
				) {
					event.preventDefault();
					caret.node.parentNode.removeChild(caret.node);
					EventManager.dispatch(this, 'input', { bubbles: true });
				}
			} else if (event.which === 46) {
				// Delete Key when caret is collapsed (no range selection is active)
				if (
					caret.offset === caret.node.textContent.length &&
					caret.node.nextSibling &&
					caret.node.nextSibling.nodeType === 1
				) {
					// Deleting a pill to the right of the cursor
					caret.node.parentNode.removeChild(caret.node.nextSibling);
					event.preventDefault();
					EventManager.dispatch(this, 'input', { bubbles: true });
				} else if (caret.offset === caret.node.textContent.length - 1) {
					// In Chrome there's a (bug? behavior?) - when deleting last character in text node, the caret jumps to the next text
					// node automatically. That's why we're doing the removal manually.
					caret.node.textContent = caret.node.textContent.substr(0, caret.node.textContent.length - 1);
					event.preventDefault();
					setCaret(caret.node, caret.node.textContent.length);
					EventManager.dispatch(this, 'input', { bubbles: true });
				}
			}
		});

		this.addEventListener('keypress', (event) => {
			if (event.which === 13) {
				// Enter (new line)
				const caret = getCaret();

				if (!caret.node || caret.node.nodeType !== 3) return;

				injectTextNode(caret.node, caret.offset, document.createTextNode('\n'));

				event.preventDefault();
				EventManager.dispatch(this, 'input', { bubbles: true });
			} else if (event.which === 59) {
				// Semicolon (;)
				const caret = getCaret();

				if (!caret.node || caret.node.nodeType !== 3) return;

				if (isNodeNestedIn('function', caret.node)) {
					injectTextNode(caret.node, caret.offset, create('imt-pill.function.separator ;'));

					event.preventDefault();
					EventManager.dispatch(this, 'input', { bubbles: true });
				}
			} else if (event.which === 41) {
				// Brace close )
				const caret = getCaret();

				if (!caret.node || caret.node.nodeType !== 3) return;

				if (isNodeNestedIn('function', caret.node)) {
					injectTextNode(caret.node, caret.offset, create('imt-pill.function.last )'));

					event.preventDefault();
					EventManager.dispatch(this, 'input', { bubbles: true });
				}
			} else if (event.which === 93) {
				// Bracket close ]
				const caret = getCaret();

				if (!caret.node || caret.node.nodeType !== 3) return;

				if (isNodeNestedIn('variable.compound', caret.node)) {
					injectTextNode(caret.node, caret.offset, create('imt-pill.variable.compound.last ]'));

					event.preventDefault();
					EventManager.dispatch(this, 'input', { bubbles: true });
				}
			} else if (event.which === 40) {
				// Brace open (
				const caret = getCaret();

				if (!caret.node || caret.node.nodeType !== 3) return;

				const name = (caret.node.textContent.substr(0, caret.offset).match(/[a-zA-Z][a-zA-Z0-9]*$/) || [])[0];
				const func = find('function', name);

				if (func && isInternal('function', name)) {
					// Ignore the internal functions
				} else if (func) {
					caret.node.textContent = `${caret.node.textContent.substr(
						0,
						caret.offset - name.length,
					)}${caret.node.textContent.substr(caret.offset)}`;
					caret.offset -= name.length;

					injectTextNode(caret.node, caret.offset, func);

					event.preventDefault();
					EventManager.dispatch(this, 'input', { bubbles: true });
				}
			} else if (event.key === '/') {
				const caret = getCaret();

				if (!caret.node || caret.node.nodeType !== 3) return;

				const coderAutoCompleteItems = createCoderAutoCompleteItems({
					cache: Coder.cache.get(this.pills),
					modules: this.pills,
					pillCategories: {
						function: Coder.functions,
						variable: Coder.variables,
						operator: Coder.operators,
						keyword: Coder.keywords,
						organization_variable: Coder.organization_variables ?? {},
						team_variable: Coder.team_variables ?? {},
						scenario_variable: Coder.scenario_variables ?? {},
						scenario_input: Coder.scenario_inputs ?? {},
					},
					imlItems: this.imlItems,
					inspector: this.form?.meta?.inspector,
					parent: caret.node?.parentNode,
				});

				this.form.tracking?.trackAutoCompleteOpen();
				this.autoComplete?.remove();
				this.autoComplete = createAutoComplete({
					items: coderAutoCompleteItems,
					pinnedItemKeys: Features.isOn('coder-ai-block') ? [MAPAI_PINNED_KEY] : [],
					onSubmit: ({ data, type }) => {
						const caret = getCaret();

						if (!caret.node || (caret.node.nodeType !== 3 && !isAiBlock(caret.node))) return;

						if (!isAiBlock(caret.node)) {
							caret.node.splitText(caret.node.offset);
						}

						this.form.tracking?.trackPillInserted({
							source: 'auto-complete',
							variableName: data.getAttribute('name'),
							variableType: type,
							isBundle: data.classList.contains('bundle-pill'),
						});

						if (
							Features.isOn('coder-ai-block') &&
							type === 'function' &&
							data.getAttribute('name') === MAPAI_FUNCTION_NAME
						) {
							const aiBlock = createAiBlock();

							if (isSpacer(caret.node.previousSibling)) {
								caret.node.previousSibling.remove();
							}
							if (isSpacer(caret.node.nextSibling)) {
								caret.node.nextSibling.remove();
							}
							caret.node.after(aiBlock);

							setCaret(aiBlock, 1);

							caret.node.remove();
						} else if (type === 'function') {
							const variablePill = createPill('function', data.getAttribute('name'), {
								imlItems: this.imlItems,
								inspector: this.form?.meta?.inspector,
							});
							const functionSignature = completeFunctionSignature(variablePill);

							caret.node.after(...functionSignature);
							setCaret(functionSignature[1], 0);
						} else {
							const textNode = document.createTextNode(data.toIML({ keepUnwantedOperators: true }));

							caret.node.after(textNode);
							setCaret(textNode, 1);
						}

						// Propagate input event to the parent
						EventManager.dispatch(this, 'input', { bubbles: true });
					},
				});

				if (!this.autoComplete) {
					return;
				}

				// Prevent default stops 'input' listener from triggering, but we still need to perform normalization
				event.preventDefault();
				normalize(this, this.panelController.variables, true, this.imlItems, this.form?.meta?.inspector);
			}
		});

		this.addEventListener('input', (event) => {
			normalize(this, this.panelController.variables, true, this.imlItems, this.form?.meta?.inspector);
		});

		this.addEventListener('copy', (event) => {
			event.preventDefault();

			const range = getRange(true);
			if (!range.length) return;

			const text = copyImlFromRange(range);
			setTextToClipboard(text, event);
		});

		this.addEventListener('cut', (event) => {
			event.preventDefault();

			const range = getRange(true);
			if (!range.length) return;

			const text = copyImlFromRange(range);
			setTextToClipboard(text, event);

			if (this.hasAttribute('readonly') || this.hasAttribute('disabled')) return;

			const selection = window.getSelection();
			selection.deleteFromDocument();

			// Propagate input event to the parent
			EventManager.dispatch(this, 'input', { bubbles: true });
		});

		this.addEventListener('paste', (event) => {
			event.preventDefault();

			if (this.hasAttribute('readonly') || this.hasAttribute('disabled') || this.hasExpandedPill) return;

			// Remove invisible characters (NBSP, zero-width spaces, BOM, soft hyphen, word joiner)
			const text = getTextFromClipboard(event).replace(/(?:\u00A0|\u00AD|\u200B|\u200C|\u200D|\u2060|\uFEFF)/g, '');
			const textNode = document.createTextNode(
				// NOTE: Text here can still theoretically contain a deeply nested AI block
				isAiBlock(event.target)
					? handleAiBlockPaste(text, {
							variables: this.panelController.variables,
							domCache: this.form ? Coder.cache.get(this.pills) : {},
							imlItems: this.imlItems,
					  })
					: text,
			);

			const selection = window.getSelection();
			const range = selection.getRangeAt(0);
			range.extractContents();
			range.insertNode(textNode);
			selection.collapseToEnd();

			// Update the caret position
			setCaret(textNode, textNode.textContent.length);

			this.scrollToCaret(textNode);

			// Propagate input event to the parent
			EventManager.dispatch(this, 'input', { bubbles: true });
		});

		this.addEventListener('drop', (event) => {
			if (this.hasAttribute('readonly') || this.hasAttribute('disabled') || this.hasExpandedPill) {
				event.preventDefault();
			}

			// sets caret after dropped item
			setTimeout(() => {
				const caret = getCaret();

				if (caret.node?.nextSibling && caret.node?.nextSibling?.nextSibling) {
					setCaret(caret.node.nextSibling.nextSibling, 1, true);
				}
			}, 0);
		});

		this.addEventListener('focusin', (event) => {
			if (this.panelEnabled && this.panelController?.state && !this.hasExpandedPill) {
				if (this.panelController.state.lastActiveCoder !== this) {
					this.panelController.state.lastActiveCoder = this;
					this.panelController.open(this.getEventTargetCoder(event));
				}
			}
		});

		this.addEventListener('click', (event) => {
			if (this.panelEnabled && !this.disabled && !this.hasExpandedPill) {
				if (!this.panelController?.panel) {
					this.panelController.state.lastActiveCoder = this;
					this.panelController.open(this.getEventTargetCoder(event));
				} else if (!this.panelController.panel.isConnected) {
					this.panelController.open(this.getEventTargetCoder(event));
				}
			}
		});

		this.addEventListener('focusout', (event) => {
			this.autoComplete?.remove();

			if (this.panelEnabled) {
				this.panelController.state.lastActiveCoderCaretOffset = getCaret().offset;

				if (!this.panelController.state.onPanel && !this.panelController.state.onSearchInput) {
					const focusTarget = event?.relatedTarget;

					if (this.panelController._hintPanel) {
						this.panelController._hideHintPanel(this.panelController._hintPanel, true);
					}

					if (focusTarget?.tagName === 'IMT-CODER' && this._form === focusTarget._form && this.panelController?.state) {
						const coder = event.relatedTarget;

						// If jumping between domains, we need to close the coder anyway, so it re-renders with correct pills
						if (coder?.domain !== this.domain) {
							this.panelController.close();
						} else {
							this.panelController.state.lastActiveCoder = coder;
							this.panelController.panel.relative = coder;
						}
					} else {
						this.panelController.close();
					}
				}
			}
			if (this.panelController?._hintPanel) {
				this.panelController._hideHintPanel(this.panelController._hintPanel, true);
			}
			if (this.panelController.state.keepFocused) {
				this.focus();
			}
		});
	}

	scrollToCaret(textNode) {
		if (textNode.parentNode) {
			const tempAnchor = document.createElement('span');

			try {
				textNode.parentNode.insertBefore(tempAnchor, textNode.nextSibling);

				const anchorRect = tempAnchor.getBoundingClientRect();
				const containerRect = this.getBoundingClientRect();

				// Check if the anchor is outside the visible bounds of the container
				if (anchorRect.bottom > containerRect.bottom || anchorRect.top < containerRect.top) {
					tempAnchor.scrollIntoView({ behavior: 'instant', block: 'center' });
				}
			} catch (e) {
				console.error(e);
			}

			tempAnchor.remove();
		} else {
			// Fallback - Adjust scrolling after pasting longer text
			this.scrollTop = this.scrollHeight;
		}
	}

	initIML() {
		if (IML && !(Coder.functions || Coder.variables || Coder.operators || Coder.keywords)) {
			this._buildImlPills();
		} else if (this.changedIml()) {
			Coder.team_variables = null;
			Coder.organization_variables = null;
			Coder.scenario_inputs = null;
			Coder.functions = null;
			this._buildImlPills();
		}
	}

	imlCustomVariables() {
		const TEAM_VARIABLES = getIMLCategory('TEAM_VARIABLES', this.imlItems);
		const ORGANIZATION_VARIABLES = getIMLCategory('ORGANIZATION_VARIABLES', this.imlItems);
		const SCENARIO_INPUTS = getIMLCategory('SCENARIO_INPUTS', this.imlItems);
		const SCENARIO_VARIABLES = getIMLCategory('SCENARIO_VARIABLES', this.imlItems);
		const CUSTOM_FUNCTIONS = this.form?.meta?.imlOptions?.functions;

		return {
			TEAM_VARIABLES,
			ORGANIZATION_VARIABLES,
			SCENARIO_INPUTS,
			SCENARIO_VARIABLES,
			CUSTOM_FUNCTIONS,
		};
	}

	changedIml() {
		const compare = this.imlCustomVariables();

		return !Coder.pillCacheValue || Coder.pillCacheValue !== JSON.stringify(compare);
	}

	connectedCallback() {
		this._coderHistory?.connect();

		if (this._built) return;
		this._built = true;

		this.initIML();

		this.classList.add('form-control');
		this.setAttribute('contenteditable', !this.hasAttribute('disabled'));
		this.setAttribute('spellcheck', 'false');
		this.setAttribute(
			'focused-placeholder',
			I18n.l(Features.isOn('coder-ai-block') ? 'common.focusedAiPlaceholder' : 'common.focusedPlaceholder'),
		);

		const rows = parseInt(this.getAttribute('rows'), 10);
		if (!isNaN(rows)) this.style.setProperty('--rows', rows.toString());

		// Try getting pills from Domain, then fallback to Form if there are none
		const input = this.closest('[field]');

		this.pills = input
			? this.form.domains.getDefaults(input.domain)?.pills || this.form.meta?.pills
			: this.form.meta.pills;
		this.variables = this._buildPills();
		this.domain = input?.domain || 'default';

		this.form.coderPanelController =
			this.form.coderPanelController || new CoderPanelController(Coder, this.form, this.imlItems);
		this.panelController = this.form.coderPanelController;

		this.panelController.variables = this.variables;
		this.panelController.pills = this.form.meta.pills;

		if (this._pendingValue) {
			this.renderValue(this._pendingValue);
			this._pendingValue = null;

			const field = this.closest('[field]');

			if (field && field._runValidators) {
				field._runValidators();
			}
		}
	}

	disconnectedCallback() {
		this._coderHistory?.disconnect();
	}

	get containsIML() {
		return this.querySelector('imt-pill') != null;
	}

	getReferencePills() {
		return Array.from(this.querySelectorAll('imt-pill:not(.last)')).filter((x) => x._pill);
	}

	get disabled() {
		return this.hasAttribute('disabled');
	}

	set disabled(value) {
		if (value) {
			this.setAttribute('disabled', '');
			this.setAttribute('contenteditable', 'false');
		} else {
			this.removeAttribute('disabled');
			this.setAttribute('contenteditable', 'true');
		}
	}

	get errors() {
		const problems = [];

		for (const pill of this.querySelectorAll('imt-pill[invalid]')) {
			problems.push(pill.getAttribute('invalid'));
		}
		return problems;
	}

	get readonly() {
		return this.hasAttribute('readonly');
	}

	/**
	 * There are multiple focus calls when clicking on pills from the panel so we need to preventScroll by default to avoid unintended scrolling.
	 * @param {FocusOptions} [options={ preventScroll: true }]
	 */
	focus(options) {
		super.focus({ preventScroll: true, ...options });
	}

	set readonly(value) {
		if (value) {
			this.setAttribute('readonly', '');
		} else {
			this.removeAttribute('readonly');
		}
	}

	get hasExpandedPill() {
		return !!this.querySelector('imt-pill[expand=true]');
	}

	get value() {
		if (this._pendingValue) {
			return this._pendingValue;
		}

		if (!this.childNodes || !this.childNodes.length) {
			return '';
		}

		if (this.childNodes.length === 1 && this.childNodes[0].nodeName === '#text') {
			return this.childNodes[0].textContent.replace(new RegExp(SPACER, 'g'), '');
		}

		return IML.stringify(domToAST(this.childNodes, true), undefined, { keepUnwantedOperators: true });
	}

	set value(value) {
		// render value on build
		if (!this._built) {
			this._pendingValue = value;
		} else {
			this.renderValue(value);
		}
	}

	get form() {
		if (!this._form) {
			this._form = this.closest('imt-forman');
		}

		return this._form;
	}

	renderValue(value) {
		// Erase content
		this.textContent = value;

		// Erase current content;
		this.innerHTML = '';

		// Attach DOM created from AST
		let ast;
		try {
			ast = IML.parse(value, undefined, undefined, { keepUnwantedOperators: true });
		} catch (e) {
			console.error(e);
			// If parsing fails, just render as plain text
			ast = IML.Array('', undefined, { keepUnwantedOperators: true });
			ast.push({ type: 'text', value: value });
		}

		for (const elm of astToDOM(
			ast,
			this.panelController.variables,
			undefined,
			false,
			this.form ? Coder.cache.get(this.pills) : {},
			this.imlItems,
			this.form?.meta?.inspector,
		)) {
			this.appendChild(elm);
		}

		normalize(this, this.panelController.variables, true, this.imlItems, this.form?.meta?.inspector);
	}

	get form() {
		if (!this._form) {
			this._form = this.closest('imt-forman');
		}

		return this._form;
	}

	get imlItems() {
		return (this.form?.meta?.imlOptions?.variables || []).concat(this.form?.meta?.imlOptions?.functions || []);
	}

	get imlCategories() {
		return this.imlItems?.map((x) => x.category) || [];
	}

	_buildImlPills() {
		IML_CATEGORIES.concat(this.imlCategories).forEach((type) => {
			const vars = getIMLCategory(type.toUpperCase(), this.imlItems);

			if (vars) {
				let items = Object.entries(vars);

				if (this.imlCategories.includes(type)) {
					items = items.sort((a, b) => sortVariables(a[1], b[1]));
				}

				items.forEach(([name, spec]) => {
					if (spec.hidden || this.isHiddenIml(type, name) || spec.type?.startsWith('account:')) {
						return;
					}

					const elm = createPill(type.slice(0, -1), name, {
						imlItems: this.imlItems,
						inspector: this.form?.meta?.inspector,
					});

					elm.setAttribute('preview', true);

					switch (type) {
						case 'functions':
							elm.textContent = name || '()';
							break;
						case 'operators':
							elm.textContent = spec.label || name;
							break;
						case 'variables':
						case 'keywords':
						case 'organization_variable':
						case 'team_variable':
							elm.textContent = name;
					}

					elm._pill = spec;

					Coder[type] = Coder[type] || {};
					Coder[type][name] = elm;
				});
			}
		});

		Coder.pillCacheValue = JSON.stringify(this.imlCustomVariables());
	}

	_buildPills() {
		let variables = {};

		const createPills = (group, nested, level = 0, parent) => {
			if (level === 0) {
				const name = '`' + group.id + '`';
				const bundlePill = createPill('variable', name, {
					imlItems: this.imlItems,
					inspector: this.form?.meta?.inspector,
				});
				const showIDs = imt?.config?.showIDs;
				const prefix = showIDs ? `${group.id}. ` : '';

				bundlePill.classList.add('bundle-pill');

				bundlePill.textContent = `${group.name} - ${group.description} [bundle]`;

				const iconSrc = getGroupIconSrc(group);
				bundlePill.style.setProperty('--bundle-icon', `url(${iconSrc})`);

				bundlePill.setAttribute('prefix', prefix);
				bundlePill.setAttribute('level', level);
				bundlePill.setAttribute('type', 'collection');
				bundlePill._pillMeta = { level, prefix, pills: group.pills };

				const theme = group?.pills?.[0].theme;
				const node = group?.pills?.[0].node;
				if (theme) {
					bundlePill.style.color = theme.determineForegroundColor();
					bundlePill.style.backgroundColor = theme.toHex();
				}

				bundlePill._pill = { ...group, ...{ theme, node, type: 'collection' } };

				variables[name] = bundlePill;
			}

			group.pills
				.filter((spec) => !variables[spec.code])
				.forEach((spec, index) => {
					const elm = createPill('variable', spec.code, {
						imlItems: this.imlItems,
						inspector: this.form?.meta?.inspector,
					});

					const showIDs = imt?.config?.showIDs;
					const prefix = showIDs && spec.node ? `${spec.node.id}. ${spec.prefix}` : '' + spec.prefix;

					elm.textContent = spec.name || spec.raw;

					elm.setAttribute('prefix', prefix);
					elm.setAttribute('level', level);
					elm.setAttribute('type', spec.type);
					elm._pillMeta = { level, prefix };

					if (nested) {
						elm.setAttribute('nested', true);
						elm.setAttribute('parent-type', group.type);
						elm.setAttribute('nth', index + 1);
						elm._pillMeta.nested = true;
						elm._pillMeta.nth = index + 1;
					}

					if (spec.theme) {
						elm.style.color = spec.theme.determineForegroundColor();
						elm.style.backgroundColor = spec.theme.toHex();
					}

					elm._pill = spec;

					if (/^(\d+)\.(.*)$/.test(spec.code)) {
						elm.sample = create(`div.coder-sample`);
						elm.sample.textContent = sample(IML._mapVariable(this.form.meta.samples, spec.code));
					}

					if (spec.pills && spec.pills.length) {
						elm.setAttribute('has-children', true);
						elm._pillMeta.hasChildren = true;
						createPills(spec, true, level + 1, elm);
					}

					variables[spec.code] = elm;
				});
		};

		const pills = this.pills;

		if (pills && pills.length) {
			if (Coder.cache.has(pills)) {
				variables = Coder.cache.get(pills);
			} else {
				pills.forEach((group) => {
					group.pills && group.pills.length && createPills(group);
				});

				Coder.cache.set(pills, variables);
			}
		}

		return variables;
	}

	isHiddenIml(type, name) {
		const hiddenImls = [{ type: 'operators', name: '++' }];

		return hiddenImls.some((x) => x.type === type && x.name === name);
	}

	// Solving cypress pill click
	getEventTargetCoder(event) {
		return event.target?.nodeName === 'IMT-CODER' ? event.target : event.target?.closest('IMT-CODER');
	}
}

Coder.functions = null;
Coder.variables = null;
Coder.operators = null;
Coder.keywords = null;
Coder.pillCacheValue = null;
Coder.cache = new WeakMap();

registerCustomElement('imt-coder', Coder);

const closeHintInActive = () => {
	if (document.activeElement.closest) {
		const coder = document.activeElement.closest('imt-coder');

		if (coder?.panelController?._hintPanel) {
			coder.panelController._hideHintPanel(coder.panelController._hintPanel, true);
		}
	}
};

// Event listeners
document.addEventListener('selectionchange', (event) => {
	const showFnHint = () => {
		const node = getCaret().node;

		if (!node) {
			closeHintInActive();
		} else {
			let current = node;
			let skip = 0;
			let position = 0;

			while (current.previousSibling) {
				if (!current || is(current, 'imt-pill.function')) {
					if (is(current, 'imt-pill.function.last')) {
						skip++;
					} else if (is(current, 'imt-pill.function.first')) {
						if (skip < 1) {
							break;
						}
						skip--;
					} else if (is(current, 'imt-pill.function.separator') && skip < 1) {
						position++;
					}
				}
				current = current.previousSibling;
			}

			if (current && is(current, 'imt-pill.function')) {
				const coder = current && current.closest && current.closest('imt-coder');
				const name = current.getAttribute('name');
				const type = current.getAttribute('type');

				let group = current.getAttribute('group');

				if (group?.includes(',')) {
					group = group.split(',')[0];
				}

				const syntax = current.classList.contains('custom-function')
					? name + `(${current.getAttribute('function-arguments')})`
					: I18n.l(`iml.${group}.functions.${name}.syntax`);

				const description = current.classList.contains('custom-function')
					? current.getAttribute('description')
					: I18n.l(`iml.${group}.functions.${name}.help`);

				const nameSegments = syntax.match(/^\s*(\w+)\s*\((.*)\)/);
				let fnName;
				let fnArgs;

				if (nameSegments) {
					fnName = nameSegments[1];
					fnArgs = nameSegments[2].split(';');
				}

				if (!coder?.fnPanelEnabled) {
					return;
				}

				if (
					coder?.panelController?._hintPanel?.relative !== current ||
					!coder?.panelController?._hintPanel?.setActiveArg
				) {
					if (coder.panelController._hintPanel) {
						coder.panelController._hideHintPanel(coder.panelController._hintPanel, true);
					}
					coder.panelController._showHintPanel(
						current,
						{
							name: fnName || syntax,
							args: fnArgs,
							activeArg: position,
							help: description,
							example: I18n.l(`iml.${group}.functions.${name}.example`, {
								defaultValue: null,
								skipInterpolation: true,
							}),
							type,
						},
						true,
						true,
						true,
					);
				} else {
					coder.panelController._hintPanel.setActiveArg(position);
				}
			} else {
				closeHintInActive();
			}
		}
	};

	// Remove .selection class from all pills
	for (const pill of document.querySelectorAll('imt-pill.selection')) {
		pill.classList.remove('selection');
	}

	showFnHint();

	// Add .selection class to all pills in selection
	const pills = getRange().filter((node) => node.nodeType === 1);

	if (!pills.length) return;

	for (const pill of pills) {
		pill.classList.add('selection');
	}
});

document.addEventListener('touchstart', (event) => {
	const panel = event.target.closest('.coder-pills');

	if (!panel) return;

	panel.controller.state.onPanel = true;
});

/** @param {PointerEvent} event */
const pointerupListener = (event) => {
	// This is only emitted when user clicked the pill but drag has not started
	const panel = event.target?.closest ? event.target?.closest('.coder-pills') : null;

	if (!panel) return;

	if (panel.controller.state.lastActiveCoder && panel.controller.state.onPanel) {
		// TODO: Is setTimeout really necessary?
		setTimeout(() => panel.controller.state.lastActiveCoder.focus(), 0);

		if (event.target.nodeName === 'IMT-PILL') {
			const nodesAffectedByDrag = resolveFullDOM(event.target);
			const iml = IML.stringify(domToAST(nodesAffectedByDrag, true), undefined, { keepUnwantedOperators: true });

			const caret = getCaret();

			if (!caret.node || caret.node.nodeType !== 3) return;

			injectTextNode(caret.node, caret.offset, document.createTextNode(iml));

			EventManager.dispatch(panel?.controller?.state?.lastActiveCoder, 'input', { bubbles: true });

			const form = event.target.closest('imt-forman');

			form?.tracking?.trackPillInserted({
				source: 'click',
				variableName: event.target.getAttribute('name'),
				variableType: event.target.getType(),
				isBundle: event.target.classList.contains('bundle-pill'),
			});
		}
	}

	if (panel.controller.state.onSearchInput && !event.target.closest('.focusable')) {
		// TODO: Add to the focus out
		panel.controller.state.lastActiveCoder.focus();

		const caret = getCaret();

		setCaret(caret.node, panel.controller.state.lastActiveCoderCaretOffset);

		panel.controller.state.onSearchInput = false;
	}

	panel.controller.state.keepFocused = false;
	panel.controller.state.onPanel = false;
};

document.addEventListener('pointerup', pointerupListener);

// Helper functions

function sortVariables(a, b) {
	if (!a || !b) {
		return 0;
	}

	if (a.isSystem && !b.isSystem) {
		return 1;
	} else if (!a.isSystem && b.isSystem) {
		return -1;
	}

	if (!a.name || !b.name) {
		return 0;
	}

	return a?.name.localeCompare(b.name);
}

/**
 * Returns where the given element lays inside a function/variable or not. It calculates the number of function/variable openings and that number
 * must be higher than function/variable ends.
 *
 * @param {string} type Type of lookup - `function` or `variable`.
 * @param {HTMLElement} elm Element to check.
 * @returns {boolean}
 */

function isNodeNestedIn(type, elm) {
	if (!elm) return false;

	let counter = 0;
	let prev = elm.previousSibling;

	while (prev) {
		if (is(prev, `imt-pill.${type}.first`)) counter++;
		if (is(prev, `imt-pill.${type}.last`)) counter--;
		prev = prev.previousSibling;
	}

	return counter > 0;
}

function find(type, name) {
	const result = Coder[type + 's'][name];

	return result ? result.cloneNode(true) : undefined;
}

function isInternal(type, name) {
	const result = Coder[type + 's'][name];

	return result._pill?.internal ?? false;
}

function sample(value) {
	if (!value) return '';

	switch (typeof value) {
		case 'string':
			if (/^IMTBuffer\((\d+), ([^,]*), ([^,]*)\): ([\s\S]*)$/.exec(value)) {
				return RegExp.$4.replace(/(.{2})/g, '$1 ');
			}

			if (/^IMTString\((\d+)\): ([\s\S]*)$/.exec(value)) {
				return RegExp.$2;
			}

			return value;

		case 'object':
			if (Array.isArray(value)) {
				return `[${value.map((item) => sample(item)).join(', ')}]`;
			}
			if (value instanceof Date) {
				/* eslint-disable no-undef */
				return format(value, 'datetime', { timezone: imt.user.timezone });
			} // TODO add format function
			return '{collection}';

		default:
			return String(value);
	}
}

function normalize(element, variables = undefined, keepUnwantedOperators = true, imlItems = null, inspector = null) {
	let caret = getCaret();
	const currentAiBlock = isAiBlock(caret.node?.parentNode) ? caret.node?.parentNode : null;
	let next = element.firstChild;
	let last = null;

	// First, remove unwanted and unnecessary nodes and join text siblings together

	while (next) {
		const child = next;

		if (child.nodeType === 3) {
			// Child is text node
			if (last && last.nodeType === 3) {
				// Last child was also a text node, join them together

				if (child.textContent.length === 0 || isSpacer(child)) {
					// Remove empty text node
					next = child.nextSibling;
					if (caret.node === child) {
						caret.node = last;
						caret.offset = last.textContent.length;
					}
					element.removeChild(child);
					continue;
				}

				if (caret.node === child) {
					// Caret is positioned inside child, move caret to last
					caret.node = last;
					caret.offset += last.textContent.length;
				}

				last.textContent += child.textContent;
				next = child.nextSibling;
				element.removeChild(child);
				continue;
			} else if (child.textContent.length === 0) {
				// First text node is empty, make it a SPACER
				child.textContent = SPACER;
			}
		} else if (child.nodeType === 1) {
			// Child is HTML element
			if (isAiBlock(child)) {
				// We need to normalize the children of the ai-block
				const result = normalize(child, variables, keepUnwantedOperators, imlItems);
				if (result && currentAiBlock === child) {
					caret = result.caret;
				}
			} else if (child.nodeName === 'IMT-CODER-TOKEN') {
				// Ignore non-text nodes from tokens, which are made to be customizable
			} else if (child.nodeName === 'IMT-PILL') {
				// Remove all non-text nodes from our pills (e.g. cased by formatting in copy/paste)
				const text = child.textContent;

				child.innerHTML = '';
				child.textContent = text;
			} else if (child.nodeName === 'BR') {
				// <br> can be merged as a newline into previous text to keep the spacing between text nodes
				const isPreviousNodeText = child.previousSibling && child.previousSibling.nodeType === 3;
				const isLastNode = !child.nextSibling;

				if (isPreviousNodeText && !isLastNode) {
					child.previousSibling.textContent += '\n';
				}

				next = child.nextSibling;
				element.removeChild(child);
				continue;
			} else {
				// Unwanted node
				if (child.childNodes.length) {
					// Replace node with its content
					next = child.firstChild;
					while (child.firstChild) {
						element.insertBefore(child.firstChild, child);
					}

					element.removeChild(child);
					continue;
				} else {
					next = child.nextSibling;
					element.removeChild(child);
					continue;
				}
			}
		} else {
			if (caret.node === child) {
				// Caret is positioned inside child, move caret to prev/next
				if (last) {
					caret.node = last;
					caret.offset = last.textContent.length;
				} else {
					if (!child.nextSibling) {
						// No next sibling, we're removing the last child, so create empty text node as placeholder
						next = document.createTextNode('');
						element.appendChild(next);
						element.removeChild(child);
						continue;
					} else {
						caret.node = child.nextSibling;
						caret.offset = 0;
					}
				}
			}

			// Unknown node, remove
			element.removeChild(child);
			continue;
		}

		last = child;
		next = child.nextSibling;
	}

	// Performance boost - break out when there's nothing to normalize

	if (element.childNodes.length === 1 && element.childNodes[0].nodeType === 3) {
		if (isSpacer(element.childNodes[0]) || isDoubleSpacer(element.childNodes[0])) {
			element.childNodes[0].textContent = '';
			if (isAiBlock(element)) {
				caret.node = element;
				caret.offset = 0;
			}
		}

		// Single empty text node, skip normalization
		if (element.childNodes[0].textContent.length === 0) return { element, caret };
	}

	// Next, find new IML nodes in text

	last = null;
	next = element.firstChild;

	while (next) {
		const child = next;

		if (child.nodeType === 3) {
			// Child is text node
			RE_IML_GLOBAL.lastIndex = 0; // Reset the lastIndex
			const result = RE_IML_GLOBAL.exec(child.textContent);

			if (result) {
				// Child contains IML expression
				let ast;

				try {
					ast = IML.parse(result[0], undefined, undefined, { keepUnwantedOperators });
				} catch (e) {
					console.error(e);
					// If parsing fails, just render as plain text
					ast = IML.Array('', undefined, { keepUnwantedOperators });
					ast.push({ type: 'text', value: result[0] });
				}

				for (const err of IML.errors) console.warn(err);
				const dom = astToDOM(ast, variables, undefined, false, Coder.cache.get(element.pills), imlItems, inspector);

				if (result.index + result[0].length === child.textContent.length) {
					// IML is on the end of the text node
					child.textContent = child.textContent.substr(0, result.index);

					if (child.textContent.length === 0 && !isAiBlock(dom[0])) {
						// IML expression was all around the text content, replace it with SPACER
						child.textContent = SPACER;
					}

					const lastDomNode = dom[dom.length - 1];

					if (!child.nextSibling) {
						// We have no sibling on the right - we must create SPACER
						if (isAiBlock(lastDomNode)) {
							lastDomNode.appendChild(document.createTextNode(SPACER));
						} else {
							element.appendChild(document.createTextNode(SPACER));
						}
					} else if (child.nextSibling.nodeType !== 3) {
						// We have no text sibling on the right - we must create SPACER
						element.insertBefore(document.createTextNode(SPACER), child.nextSibling);
					}

					if (caret.node === child && caret.offset > result.index && !isAiBlock(lastDomNode)) {
						// Cursor was originally in the place where the code was
						caret.node = child.nextSibling;
						caret.offset = 0;
					} else if (isAiBlock(lastDomNode)) {
						caret.node = lastDomNode.lastChild;
						caret.offset = 0;
					}

					// We need to store next sibling as constand because it's about to change once we start adding new dom
					const nextSibling = child.nextSibling;

					for (const elm of dom) {
						if (isAiBlock(elm) && (child.textContent.length === 0 || child.textContent === SPACER)) {
							child.remove();
						}

						element.insertBefore(elm, nextSibling);
					}

					last = lastDomNode;
					next = last && last.nextSibling;
					continue;
				} else if (result.index === 0) {
					// IML is right on the text start
					child.textContent = child.textContent.substr(result[0].length);

					if (child.textContent.length === 0) {
						// IML expression was all around the text content, replace it with SPACER
						child.textContent = SPACER;
					}

					if (!child.previousSibling || child.previousSibling.nodeType !== 3) {
						// We have no text sibling on the left - we must create SPACER
						element.insertBefore(document.createTextNode(SPACER), child);
					}

					if (caret.node === child) {
						// Cursor was originally in the place where the code was
						caret.offset = Math.max(0, caret.offset - result[0].length);
					}

					for (const elm of dom) {
						element.insertBefore(elm, child);
					}

					// Iterate trought the child again to find another code occurences
					next = child;
					last = child.previousSibling;
					continue;
				} else {
					next = child.splitText(result.index);
					next.textContent = next.textContent.substr(result[0].length);

					if (caret.node === child && caret.offset > result.index) {
						// Cursor was originally in the place where the code was
						caret.node = next;
						caret.offset = Math.max(0, caret.offset - (result.index + result[0].length));
					}

					for (const elm of dom) {
						element.insertBefore(elm, next);
					}

					last = next.previousSibling;
					continue;
				}
			}
		}

		last = child;
		next = child.nextSibling;
	}

	// Last, inject pill siblings and edges with invisible SPACER

	last = null;
	next = element.firstChild;

	while (next) {
		const child = next;

		if (child.nodeType === 3) {
			// Child is text node
			if (isSpacer(child)) {
				if (
					(child.previousSibling && child.previousSibling.nodeType !== 1) ||
					(child.nextSibling && child.nextSibling.nodeType !== 1)
				) {
					// Remove SPACER when not between two elements (except edges)
					next = child.nextSibling;
					element.removeChild(child);
					continue;
				}
			} else if (isDoubleSpacer(child)) {
				child.textContent = SPACER;
				if (caret.node === child && caret.offset > 1) caret.offset--;
			} else {
				// Search for SPACERs and remove unwanted ones
				let cursor = child.textContent.indexOf(SPACER);
				const originalOffset = caret.offset;

				// There's a SPACER inside text node, clean it up (ignore last character bacuse it is probably SPACER)
				while (cursor !== -1 && cursor < child.textContent.length) {
					if (cursor === 0 && child.textContent.charAt(1) === '\n') {
						// SPACER on the first position followed by newline - SPACER stays
						cursor = child.textContent.indexOf(SPACER, 1);
						continue;
					} else if (
						cursor === child.textContent.length - 1 &&
						child.textContent.charAt(child.textContent.length - 2) === '\n'
					) {
						// SPACER on the last position preceded by newline - SPACER stays
						break;
					}

					// Caret is inside this child
					if (caret.node === child && cursor < originalOffset) caret.offset--;

					// Remove unnecessary SPACER
					child.textContent = child.textContent.substr(0, cursor) + child.textContent.substr(cursor + 1);

					cursor = child.textContent.indexOf(SPACER, cursor);
				}

				if (child.textContent.charAt(0) === '\n') {
					child.textContent = SPACER + child.textContent;
					if (caret.node === child && caret.offset > 0) caret.offset += 1;
				}
				if (child.textContent.charAt(child.textContent.length - 1) === '\n') {
					child.textContent = child.textContent + SPACER;
				}
			}
		} else if (child.nodeType === 1) {
			// Child is HTML element
			if (last && last.nodeType === 1 && !isAiBlock(last)) {
				// Pill siblings, inject SPACER
				element.insertBefore(document.createTextNode(SPACER), child);
			}
		}

		last = child;
		next = child.nextSibling;
	}

	if (element.firstChild && element.firstChild.nodeType === 1 && !isAiBlock(element.firstChild)) {
		// HTML element on the start, add SPACER
		element.insertBefore(document.createTextNode(SPACER), element.firstChild);
	}

	if (element.lastChild && element.lastChild.nodeType === 1 && !isAiBlock(element.lastChild)) {
		// HTML element on the end, add SPACER
		element.appendChild(document.createTextNode(SPACER));
	}

	if (document.activeElement === element) {
		// This coder is focused, make sure we move caret accordingly to changes
		if (caret.node) {
			setCaret(caret.node, caret.offset);
		} else {
			// In case we have no anchor, seek to last possible caret position
			if (element.lastChild) {
				setCaret(element.lastChild, element.lastChild.textContent.length);
			}
		}
	}

	return { element, caret };
}
